import { Copy, Download, FileUp, Share } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { exportCsv, exportRelationshipsCsv } from '../../io/csv';
import { DEFAULT_EXPORT_OPTIONS, exportGedcom } from '../../io/gedcom-export';
import { flateImage, pdfDocument, planPages, type PageSize, type PdfPage, type Tile } from '../../io/pdf';
import { withDpi } from '../../io/png';
import { INCLUDE_EVERYTHING, includesEverything, PRIVACY_LABELS, redactTree, type ExportPrivacy } from '../../io/redact';
import { displayName, isHiddenName, lifeStatus, shortName } from '../../model/people';
import { canvasBlob, chartSvg, cropSvg, CSS_VAR_COLORS, printScale, rasterize, safeScale, viewOptions, wholeTreeSvg, type ChartHeading } from '../export/chart';
import { CARD_FIELDS, loadCardFields, type CardField } from '../chart/cards';
import { htmlPage, type Appearance } from '../export/html';
import { loadFonts, needsPicture, prepareAssets, vectorPdf, type VectorFonts } from '../export/vector-pdf';
import { findTheme, paletteOf, paletteTokens } from '../themes';
import { importGedcomBytes, type ImportSummary } from '../../io/gedcom-import';
import { importMacFamilyTree, type PackageFiles } from '../../io/macfamilytree';
import { blobToDataUrl, exportNative, importNativeFile, looksNative, NATIVE_EXTENSION } from '../../io/native';
import { findPackage, looksLikeZip, readDirectoryEntry, readZip } from '../../io/package-files';
import { looksLikeSqlite } from '../../io/sqlite';
import { getFile, lsGet, lsSet, putFiles } from '../../model/db';
import type { FileBag } from '../../model/files';
import { mergeTrees } from '../../model/tree';
import type { Id, PartnerRel, PartnerType, Tree } from '../../model/types';
import { PARTNER_TYPE_LABELS, plural } from '../labels';
import { photoFromBlob, thumbFromBlob } from '../photo';
import { adoptTree, closeDialog, commit, getState, markBackedUp, toast, undo, useApp } from '../store';
import { Modal, Segmented, Select } from './common';

export function saveFile(name: string, content: string, mime: string) {
  saveBlob(name, new Blob([content], { type: mime }));
}

export function saveBlob(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function safeFileName(name: string): string {
  return name.replace(/[‐-―]/g, '-').replace(/[^\p{L}\p{N} _.-]+/gu, '').trim().replace(/\s+/g, '-').replace(/-{2,}/g, '-') || 'family-tree';
}

export interface Parsed {
  tree: Tree;
  summary?: ImportSummary;
  kind: 'gedcom' | 'native' | 'macfamilytree';
  fileName: string;
  /** Media files that came with the tree, saved when it's opened or added. */
  files?: FileBag;
}

const PACKAGE_HELP = 'This browser can’t open a .mftpkg package from the file picker. Drag the package onto this window instead, or compress it in Finder (right-click, then Compress) and choose the .zip.';

export async function parseFile(file: File): Promise<Parsed> {
  let buf: Uint8Array;
  try {
    buf = new Uint8Array(await file.arrayBuffer());
  } catch {
    // Chrome hands over a package (a folder) from its file picker as an unreadable file.
    throw new Error(/\.mftpkg$/i.test(file.name) ? PACKAGE_HELP : `Couldn’t read “${file.name}”.`);
  }
  if (looksLikeZip(buf)) {
    const pkg = findPackage(readZip(buf), file.name);
    if (!pkg) throw new Error('This zip file doesn’t contain a MacFamilyTree package (.mftpkg).');
    return parseMacFamilyTree(pkg);
  }
  if (looksLikeSqlite(buf)) {
    // Just the database from inside a package: everything but the photos.
    const name = file.name === 'database' ? 'MacFamilyTree import' : file.name.replace(/\.[^.]*$/, '');
    const parsed = await parseMacFamilyTree({ name, read: async (p) => (p === 'database' ? buf : undefined) });
    parsed.summary!.warnings.push('Only the database file was opened, so photos weren’t included. Drag in the whole .mftpkg package to bring them too.');
    return parsed;
  }
  if (/\.mftpkg$/i.test(file.name)) throw new Error(PACKAGE_HELP);
  const head = new TextDecoder('utf-8').decode(buf.subarray(0, 512));
  if (/\.json$/i.test(file.name) || looksNative(head)) {
    const { tree, files } = importNativeFile(new TextDecoder('utf-8').decode(buf));
    return { tree, files, kind: 'native', fileName: file.name };
  }
  const { tree, summary } = importGedcomBytes(buf, file.name);
  if (!summary.people && !/HEAD/.test(head)) throw new Error("This doesn't look like a GEDCOM (.ged), MacFamilyTree (.mftpkg), or Relationships (.json) file.");
  return { tree, summary, kind: 'gedcom', fileName: file.name };
}

/**
 * Read whatever was dropped: a file, or a folder such as a .mftpkg package. Call it during the
 * drop event itself, since the browser only allows reading the dropped items until it ends.
 */
export function parseDrop(dt: DataTransfer): Promise<Parsed> | null {
  const entry = dt.items?.[0]?.webkitGetAsEntry?.();
  if (entry?.isDirectory) {
    return readDirectoryEntry(entry as FileSystemDirectoryEntry).then((files) => {
      const pkg = findPackage(files, entry.name);
      if (!pkg) throw new Error('That folder isn’t a MacFamilyTree package. Drop a .mftpkg package, a GEDCOM (.ged) file, or a Relationships file.');
      return parseMacFamilyTree(pkg);
    });
  }
  const file = dt.files[0];
  return file ? parseFile(file) : null;
}

/** Run `work` over `items` a few at a time; big trees can have thousands of pictures. */
async function eachLimited<T>(items: T[], work: (item: T) => Promise<void>, limit = 6) {
  const queue = [...items];
  const worker = async () => {
    for (let x = queue.shift(); x !== undefined; x = queue.shift()) await work(x);
  };
  await Promise.all(Array.from({ length: limit }, worker));
}

async function parseMacFamilyTree(pkg: PackageFiles): Promise<Parsed> {
  const { tree, summary, photos, files, thumbs } = await importMacFamilyTree(pkg);
  // Profile photos, cropped to the person's face when the picture shows several people.
  let failed = 0;
  await eachLimited(photos, async (ph) => {
    try {
      tree.people[ph.personId].photo = await photoFromBlob(files.get(ph.key)!, 256, ph.region);
    } catch {
      failed++;
    }
  });
  summary.photos = photos.length - failed;
  if (failed) summary.warnings.push(`${plural(failed, 'photo')} couldn’t be read by this browser and ${failed === 1 ? 'was' : 'were'} left out.`);
  await addThumbs(tree, files, thumbs);
  return { tree, summary, files, kind: 'macfamilytree', fileName: `${pkg.name}.mftpkg` };
}

/** Gallery thumbnails for imported media: from the picture itself, or a preview image that came with it. */
async function addThumbs(tree: Tree, files: FileBag, previews = new Map<string, Blob>()) {
  const items = Object.values(tree.media).filter((m) => !m.thumb && (previews.has(m.id) || (m.kind === 'image' && m.file && files.has(m.file.key))));
  await eachLimited(items, async (m) => {
    try {
      m.thumb = await thumbFromBlob(previews.get(m.id) ?? files.get(m.file!.key)!);
    } catch {
      /* the gallery shows an icon instead */
    }
  });
}

export function ImportDialog({ initial, mode }: { initial?: Parsed; mode: 'merge' | 'new' }) {
  const [parsed, setParsed] = useState<Parsed | null>(initial ?? null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  // Couples with nothing on record about what kind of couple they are: one choice for all, plus exceptions.
  const [coupleType, setCoupleType] = useState<PartnerType>('partner');
  const [coupleOverrides, setCoupleOverrides] = useState<Record<Id, PartnerType>>({});

  const handle = async (pending: Promise<Parsed> | null) => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      setParsed(await pending);
      setCoupleType('partner');
      setCoupleOverrides({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const s = parsed?.summary;
  const current = useApp((st) => st.tree);
  const merging = mode === 'merge' && !!current;
  // What adding the file would do, worked out up front so the dialog can say so.
  const preview = useMemo(() => (merging && parsed && current ? mergeTrees(current, parsed.tree) : null), [merging, parsed, current]);
  const unlabeled = (s?.unlabeledCouples ?? []).filter((id) => parsed?.tree.relationships[id]?.kind === 'partner');
  /** The file's tree with the chosen kind of couple applied to the unlabeled couples. */
  const chosen = (t: Tree): Tree => {
    if (!unlabeled.length) return t;
    const relationships = { ...t.relationships };
    for (const id of unlabeled) relationships[id] = { ...(relationships[id] as PartnerRel), type: coupleOverrides[id] ?? coupleType };
    return { ...t, relationships };
  };
  const openNew = async () => {
    if (!parsed || busy) return;
    setBusy(true);
    await adoptTree(chosen(parsed.tree), parsed.files);
    closeDialog();
    toast(`Opened “${parsed.tree.name}”.`, 'success');
  };
  const merge = async () => {
    if (!parsed || busy) return;
    setBusy(true);
    if (parsed.files?.size) await putFiles(parsed.files);
    const m = mergeTrees(getState().tree!, chosen(parsed.tree));
    commit(m.tree);
    closeDialog();
    toast(`Added ${plural(m.added, 'person', 'people')} to ${m.tree.name}${m.matched ? ` and updated ${plural(m.matched, 'person', 'people')} already here` : ''}.`, 'success', { label: 'Undo', run: undo });
  };
  return (
    <Modal
      title={merging ? `Add a file to ${current!.name}` : 'Import a family tree'}
      footer={
        <>
          {merging && (
            <button className="btn left" disabled={!parsed || busy} onClick={openNew}>
              Open as a new tree
            </button>
          )}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" disabled={!parsed || busy} onClick={merging ? merge : openNew}>
            {merging ? 'Add to this tree' : 'Open as a new tree'}
          </button>
        </>
      }
    >
      <div className="stack">
        <div
          className={`dropzone${over ? ' over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            handle(parseDrop(e.dataTransfer));
          }}
        >
          <FileUp size={26} />
          <p style={{ margin: '8px 0' }}>Drop a GEDCOM (.ged), MacFamilyTree (.mftpkg), or Relationships ({NATIVE_EXTENSION}) file here</p>
          <button className="btn" onClick={() => input.current?.click()} disabled={busy}>
            {busy ? 'Reading…' : 'Choose a file'}
          </button>
          <input ref={input} type="file" accept=".ged,.gedcom,.json,.GED,.mftpkg,.zip,text/plain,application/json,application/zip" hidden onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) handle(parseFile(f));
            }} />
        </div>
        <p className="faint small-text">
          {merging
            ? `Everyone in the file is added to ${current!.name}. People already here (the same person from an earlier export, or the same name and full birth date) are combined rather than duplicated. You can undo it.`
            : 'The file opens as a separate tree, alongside your others.'}{' '}
          Files are read in your browser. Nothing is uploaded anywhere.
        </p>
        <p className="faint small-text">From MacFamilyTree, drag the .mftpkg package here, or compress it in Finder and choose the .zip.</p>
        {error && <div className="callout danger">{error}</div>}
        {parsed && (
          <div className="callout">
            <b>{parsed.fileName}</b>
            <div style={{ marginTop: 4 }}>
              {plural(Object.keys(parsed.tree.people).length, 'person', 'people')}, {plural(Object.keys(parsed.tree.relationships).length, 'relationship')}
              {Object.keys(parsed.tree.groups).length ? `, ${plural(Object.keys(parsed.tree.groups).length, 'group')}` : ''}.
            </div>
            {preview && (
              <div style={{ marginTop: 4 }}>
                <b>
                  Adds {plural(preview.added, 'new person', 'new people')}
                  {preview.matched ? `; ${plural(preview.matched, 'person', 'people')} already here will be updated` : ''}.
                </b>
              </div>
            )}
            {s && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }} className="small-text">
                {parsed.kind === 'macfamilytree' ? (
                  <li>
                    MacFamilyTree package{s.photos ? `, with ${plural(s.photos, 'profile photo')}` : ''}
                  </li>
                ) : (
                  <li>
                    GEDCOM {s.version ?? '(version unknown)'}
                    {s.source ? ` from ${s.source}` : ''}
                    {s.encoding ? `, ${s.encoding}` : ''}
                  </li>
                )}
                <li>
                  {plural(s.partnerships, 'partnership')}, {plural(s.parentLinks, 'parent–child link')}
                  {s.socialLinks ? `, ${plural(s.socialLinks, 'friendship or association', 'friendships or associations')}` : ''}
                </li>
                {(s.sources || s.media || s.todos) ? (
                  <li>
                    {[
                      s.sources ? `${plural(s.sources, 'source')}${s.citations ? ` (cited ${plural(s.citations, 'time')})` : ''}` : '',
                      s.media ? plural(s.media, 'media item') : '',
                      s.todos ? plural(s.todos, 'to-do') : '',
                    ].filter(Boolean).join(', ')}
                    {parsed.kind === 'gedcom' && s.media ? '. A GEDCOM file doesn’t contain the media files themselves, so add them from the Media view.' : ''}
                  </li>
                ) : null}
                {s.hiddenReligiousRecords > 0 && (
                  <li>
                    {plural(s.hiddenReligiousRecords, 'religious record')} (baptisms, christenings, and similar) {s.hiddenReligiousRecords === 1 ? 'is' : 'are'} kept for re-export but not shown.
                  </li>
                )}
                {s.preservedRecords > 0 &&
                  (parsed.kind === 'macfamilytree' ? (
                    <li>{plural(s.preservedRecords, 'identification number')} (such as Social Security numbers) {s.preservedRecords === 1 ? 'is' : 'are'} kept for GEDCOM export but not shown.</li>
                  ) : (
                    <li>{plural(s.preservedRecords, 'other record')} (repositories, notes, and others) {s.preservedRecords === 1 ? 'is' : 'are'} preserved for re-export.</li>
                  ))}
                {s.notImported && s.notImported.length > 0 && <li>Not brought over: {s.notImported.join(', ')}.</li>}
              </ul>
            )}
            {s && s.warnings.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary className="small-text" style={{ cursor: 'pointer', color: 'var(--warn)' }}>
                  {s.warnings.length} warning{s.warnings.length === 1 ? '' : 's'}
                </summary>
                <ul className="small-text" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {s.warnings.slice(0, 50).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {parsed && unlabeled.length > 0 && (
          <div className="callout stack tight">
            <b>
              {plural(unlabeled.length, 'couple')} {unlabeled.length === 1 ? 'has' : 'have'} no marriage, divorce, or engagement on record.
            </b>
            <label className="row wrap">
              <span>Bring {unlabeled.length === 1 ? 'them' : 'all of them'} in as</span>
              <select className="select" style={{ width: 'auto' }} value={coupleType} onChange={(e) => setCoupleType(e.target.value as PartnerType)}>
                {Object.entries(PARTNER_TYPE_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            {unlabeled.length > 1 && (
              <details>
                <summary className="small-text" style={{ cursor: 'pointer' }}>
                  Set couples one at a time{Object.keys(coupleOverrides).length ? ` (${Object.keys(coupleOverrides).length} set)` : ''}
                </summary>
                <div className="couple-list">
                  {unlabeled.map((id) => {
                    const r = parsed.tree.relationships[id];
                    const name = (pid: Id) => (parsed.tree.people[pid] ? displayName(parsed.tree.people[pid]) : 'Someone');
                    return (
                      <label key={id} className="couple-row">
                        <span className="grow">
                          {name(r.a)} and {name(r.b)}
                        </span>
                        <select
                          className="select"
                          value={coupleOverrides[id] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value as PartnerType | '';
                            setCoupleOverrides(({ [id]: _, ...rest }) => (v ? { ...rest, [id]: v } : rest));
                          }}
                        >
                          <option value="">Same as above ({PARTNER_TYPE_LABELS[coupleType]})</option>
                          {Object.entries(PARTNER_TYPE_LABELS).map(([k, l]) => (
                            <option key={k} value={k}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  })}
                </div>
              </details>
            )}
            <span className="faint small-text">“Partners” is the general term. You can change any couple later from their details.</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

type Format = 'native' | 'gedcom' | 'csv' | 'pdf' | 'png' | 'svg' | 'html';
type GedcomVersion = '5.5.1' | '7.0';
type Sheet = 'people' | 'relationships';
/** The whole tree, or the chart as the Tree view shows it around one person. */
type Who = 'tree' | 'view';
type Paper = 'fit' | 'letter' | 'a4' | 'letter-tiled' | 'a4-tiled';

const FORMATS: { value: Format; title: string; desc: string; group: 'data' | 'picture'; mime: string }[] = [
  { value: 'native', group: 'data', mime: 'application/json', title: 'Relationships file (.json)', desc: 'Everything, exactly as it is here, with media files. Best for backups and moving between devices.' },
  { value: 'gedcom', group: 'data', mime: 'text/plain', title: 'GEDCOM (.ged)', desc: 'For other genealogy programs.' },
  { value: 'csv', group: 'data', mime: 'text/csv', title: 'Spreadsheet (.csv)', desc: 'For Excel, Numbers, or Google Sheets.' },
  { value: 'html', group: 'picture', mime: 'text/html', title: 'Web page (.html)', desc: 'Everything in one page that opens in any browser: a chart of everyone, every person’s details and relationships, groups, sources, media, and to-dos.' },
  { value: 'pdf', group: 'picture', mime: 'application/pdf', title: 'PDF (.pdf)', desc: 'The family chart for printing, sharp at any size, with text you can search and copy.' },
  { value: 'png', group: 'picture', mime: 'image/png', title: 'Image (.png)', desc: 'The family chart as a picture, for messages, slides, and documents.' },
  { value: 'svg', group: 'picture', mime: 'image/svg+xml', title: 'Vector image (.svg)', desc: 'The family chart, sharp at any size. Opens in browsers and design apps.' },
];

/** What the dialog remembers between exports. Hidden names are never remembered: each export asks again. */
interface ExportPrefs {
  format: Format;
  privacy: Omit<ExportPrivacy, 'hiddenNames'>;
  gedcom: GedcomVersion;
  sheet: Sheet;
  who: Who;
  scale: number;
  paper: Paper;
  /** Pictures (PDF, PNG, SVG) and the web page each keep their own. */
  look: 'light' | 'dark';
  pageLook: Appearance;
  /** What each card shows; undefined follows the Tree view. */
  fields?: CardField[];
  htmlChart: boolean;
  includePreserved: boolean;
}

const PREFS_KEY = 'relationships.exportOptions';
const { hiddenNames: _hidden, ...DEFAULT_PRIVACY } = INCLUDE_EVERYTHING;
const DEFAULT_PREFS: ExportPrefs = { format: 'native', privacy: DEFAULT_PRIVACY, gedcom: '5.5.1', sheet: 'people', who: 'tree', scale: 2, paper: 'fit', look: 'light', pageLook: 'auto', htmlChart: true, includePreserved: DEFAULT_EXPORT_OPTIONS.includePreserved };

function loadPrefs(): ExportPrefs {
  try {
    const saved = JSON.parse(lsGet(PREFS_KEY) ?? '{}') as Partial<ExportPrefs>;
    const out = { ...DEFAULT_PREFS, ...saved, privacy: { ...DEFAULT_PRIVACY, ...saved.privacy } };
    if (!FORMATS.some((f) => f.value === out.format)) out.format = DEFAULT_PREFS.format;
    delete (out.privacy as Partial<ExportPrivacy>).hiddenNames;
    return out;
  } catch {
    return DEFAULT_PREFS;
  }
}

/** "2026-10-13", for file names that sort by date. */
const isoDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Whether this device can hand a file of this type to other apps (the share sheet). */
function canShareFiles(mime: string): boolean {
  try {
    return typeof navigator.canShare === 'function' && navigator.canShare({ files: [new File(['x'], 'x', { type: mime })] });
  } catch {
    return false;
  }
}

const canCopyImage = () => typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;

interface Built {
  name: string;
  blob: Blob;
  /** Said after the file name in the confirmation ("3 pages"). */
  detail?: string;
}

export function ExportDialog({ picture }: { picture?: boolean }) {
  const tree = useApp((s) => s.tree)!;
  const focusId = useApp((s) => s.focusId);
  const settings = useApp((s) => s.settings);
  const [prefs] = useState(loadPrefs);
  // From the Tree view's menu, start on a chart format.
  const [format, setFormat] = useState<Format>(picture && !['pdf', 'png', 'svg'].includes(prefs.format) ? 'pdf' : prefs.format);
  const [privacy, setPrivacy] = useState<ExportPrivacy>({ ...prefs.privacy, hiddenNames: false });
  const [hiddenTouched, setHiddenTouched] = useState(false);
  const [includePreserved, setIncludePreserved] = useState(prefs.includePreserved);
  const [gedcom, setGedcom] = useState<GedcomVersion>(prefs.gedcom);
  const [sheet, setSheet] = useState<Sheet>(prefs.sheet);
  const [who, setWho] = useState<Who>(prefs.who);
  const [scale, setScale] = useState(prefs.scale);
  const [paper, setPaper] = useState<Paper>(prefs.paper);
  const [look, setLook] = useState(prefs.look);
  const [pageLook, setPageLook] = useState<Appearance>(prefs.pageLook);
  const [fields, setFields] = useState<CardField[]>(() => prefs.fields ?? loadCardFields());
  const [htmlChart, setHtmlChart] = useState(prefs.htmlChart);
  const [busy, setBusy] = useState<false | 'download' | 'share' | 'copy'>(false);
  const hasHidden = Object.values(tree.people).some((p) => p.names.some(isHiddenName));
  // "Hide details of living people" covers only people recorded as living; these keep their details.
  const unrecorded = useMemo(() => Object.values(tree.people).filter((p) => lifeStatus(p) === 'unknown').length, [tree]);
  const focus = focusId && tree.people[focusId] ? focusId : tree.homePersonId && tree.people[tree.homePersonId] ? tree.homePersonId : Object.keys(tree.people)[0];
  // Backups keep hidden names unless told otherwise; files for sharing leave them out.
  const effective: ExportPrivacy = { ...privacy, hiddenNames: hiddenTouched ? privacy.hiddenNames : format === 'native' };
  const info = FORMATS.find((f) => f.value === format)!;
  const picFormat = format === 'pdf' || format === 'png' || format === 'svg';
  const wantsChart = !!focus && (picFormat || (format === 'html' && htmlChart));
  // The web page always charts everyone; the pictures can chart one person's part.
  const whole = format === 'html' || who === 'tree';

  const privacyKey = JSON.stringify(effective);
  const t = useMemo(() => redactTree(tree, effective), [tree, privacyKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const theme = findTheme(settings.themeId);
  const palette = (dark: boolean) => paletteTokens(paletteOf(theme, dark));
  const dark = format === 'html' ? pageLook === 'dark' : look === 'dark';
  const colors = useMemo(() => palette(dark), [theme, dark]); // eslint-disable-line react-hooks/exhaustive-deps
  const heading = (): ChartHeading => ({ title: tree.name, around: whole ? 'Everyone in the tree' : `Around ${shortName(t.people[focus!])}`, legend: true });
  const chart = useMemo(() => {
    if (!wantsChart) return null;
    const style = { colors, fields, heading: heading() };
    return whole ? wholeTreeSvg(t, style) : chartSvg(t, focus!, viewOptions(), style);
  }, [wantsChart, whole, t, focus, colors, fields, tree.name]); // eslint-disable-line react-hooks/exhaustive-deps
  // The PDF's font, loaded when a PDF is chosen, to say ahead which labels it can't set.
  const [pdfFonts, setPdfFonts] = useState<VectorFonts>();
  useEffect(() => {
    if (format === 'pdf') loadFonts().then(setPdfFonts, () => setPdfFonts(undefined));
  }, [format]);
  const pictured = useMemo(() => (chart && pdfFonts && format === 'pdf' ? chart.shapes.filter((sh) => sh.kind === 'text' && needsPicture(pdfFonts, sh)).length : 0), [chart, pdfFonts, format]);
  const [size, tiledFlag] = paper.split('-') as [PageSize, string?];
  const plan = useMemo(() => (chart && format === 'pdf' ? planPages(chart.width, chart.height, size, !!tiledFlag) : null), [chart, format, size, tiledFlag]);

  // The preview is the real picture, shown small.
  const [previewUrl, setPreviewUrl] = useState<string>();
  useEffect(() => {
    if (!chart) return setPreviewUrl(undefined);
    const url = URL.createObjectURL(new Blob([chart.svg], { type: 'image/svg+xml' }));
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [chart]);

  const count = (n: number, one: string, many?: string) => (n ? plural(n, one, many) : '');
  const andList = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')}, and ${xs[xs.length - 1]}` : xs[0]);
  const summary = (() => {
    const people = Object.keys(t.people).length;
    const contents = andList(
      [
        plural(people, 'person', 'people'),
        count(Object.keys(t.relationships).length, 'relationship'),
        count(Object.keys(t.groups).length, 'group'),
        count(Object.keys(t.sources ?? {}).length, 'source'),
        count(Object.keys(t.media ?? {}).length, 'media item'),
        count(Object.keys(t.todos ?? {}).length, 'to-do'),
      ].filter(Boolean),
    );
    const charted = chart ? `${plural(chart.people, 'person', 'people')} in the chart${chart.truncated ? ' (the most it can show)' : ''}` : '';
    if (format === 'csv') {
      if (sheet === 'people') return `${plural(people, 'row')}, one for each person.`;
      const memberships = effective.friendsAndGroups ? Object.values(t.groups).reduce((n, g) => n + g.members.filter((m) => t.people[m.personId]).length, 0) : 0;
      return `${plural(Object.keys(t.relationships).length + memberships, 'row')}, one for each relationship${memberships ? ' and group membership' : ''}.`;
    }
    if (format === 'html') return `Includes ${contents}${charted ? `, with ${charted}` : ''}.`;
    if (!picFormat) return `Includes ${contents}.`;
    if (!chart) return 'There’s no one in the tree to chart yet.';
    if (format === 'png') {
      const s = safeScale(chart.width, chart.height, scale);
      return `${charted}; ${Math.round(chart.width * s).toLocaleString('en-US')} × ${Math.round(chart.height * s).toLocaleString('en-US')} pixels.`;
    }
    if (format === 'pdf' && plan) return `${charted}; ${plural(plan.tiles.length, 'page')}${plan.tiles.length > 1 ? `, ${plan.cols} across and ${plan.rows} down` : ''}.`;
    return `${charted}.`;
  })();

  /** What this export leaves out or can't do, said plainly. `warn` marks the ones that make it worse to use. */
  const limits = ((): { text: string; warn?: boolean }[] => {
    const out: { text: string; warn?: boolean }[] = [];
    const people = Object.keys(t.people).length;
    const withFiles = Object.values(t.media ?? {}).filter((m) => m.file).length;
    if (chart?.parts && chart.parts > 1)
      out.push({ text: `Wide families are split into ${chart.parts} parts, laid out one under another like the rows below a periodic table. A dashed card marks where each part joins, and the people it hangs from are shown again, dashed, at its top.` });
    if (chart?.truncated)
      out.push({ text: `A chart can hold ${chart.people.toLocaleString('en-US')} people at most, so ${plural(people - chart.people, 'person isn’t', 'people aren’t')} in it${format === 'html' ? ', though everyone is in the directory below it' : ''}.`, warn: true });
    if (format === 'png' && chart) {
      const s = safeScale(chart.width, chart.height, scale);
      if (s < scale - 0.01)
        out.push({
          text: `Browsers can’t make a picture this big at the chosen size (16,000 pixels on a side at most), so it’s made at ${Math.round((s / scale) * 100)}% of that.${s < 0.75 ? ' Names will be hard to read; a PDF on pages at full size, or an SVG, keeps them sharp.' : ''}`,
          warn: s < 0.75,
        });
    }
    if (format === 'pdf' && plan && chart) {
      const tile = plan.tiles[0];
      const nameSize = 13.5 * tile.k;
      const inches = (pt: number) => Math.round(pt / 72);
      if (plan.tiles.length === 1 && nameSize < 6)
        out.push({ text: `On one page, names print about ${nameSize < 1 ? 'less than 1 point' : `${nameSize.toFixed(1)} points`} tall (book text is about 10), too small to read on paper, though they stay sharp when zoomed in on screen. “Pages at full size” prints them readably.`, warn: true });
      if (size === 'fit' && (plan.width > 17 * 72 || plan.height > 17 * 72))
        out.push({ text: `The page is ${inches(plan.width)} × ${inches(plan.height)} inches, bigger than most printers take. It’s made for zooming in on screen; to print, choose pages at full size.` });
      if (pictured)
        out.push({ text: `${plural(pictured, 'label uses', 'labels use')} characters the PDF’s font (Noto Sans) doesn’t have, such as Chinese, Japanese, Korean, or emoji, so ${pictured === 1 ? 'it’s' : 'they’re'} drawn as very sharp pictures instead. ${pictured === 1 ? 'It' : 'They'} can still be searched and copied.` });
      if (plan.tiles.length > 40) out.push({ text: `That’s ${plural(plan.tiles.length, 'page')} to print and tape together.` });
    }
    if (format === 'html' && withFiles && effective.media)
      out.push({ text: `The page shows each media item’s thumbnail and details, but not the ${plural(withFiles, 'file')} themselves. A Relationships file keeps them.` });
    if (format === 'gedcom' && withFiles && effective.media) out.push({ text: `GEDCOM lists media items but can’t hold the ${plural(withFiles, 'file')} themselves.` });
    return out;
  })();

  /** The "what to include" choices that change anything in this format. */
  const relevant = ((): Set<keyof ExportPrivacy> => {
    const all = PRIVACY_LABELS.map((l) => l.key);
    if (format === 'native' || format === 'html') return new Set(all);
    if (format === 'gedcom') return new Set(all.filter((k) => k !== 'photos'));
    if (format === 'csv')
      return new Set<keyof ExportPrivacy>(sheet === 'people' ? ['hiddenNames', 'identity', 'dates', 'places', 'otherEvents', 'notes', 'tags', 'friendsAndGroups'] : ['dates', 'places', 'notes', 'friendsAndGroups']);
    // A chart shows only what its cards show; names on cards are never hidden names.
    const out = new Set<keyof ExportPrivacy>();
    const has = (f: CardField) => fields.includes(f);
    if (has('photo')) out.add('photos');
    if (has('pronouns') || has('gender')) out.add('identity');
    if (has('dates') || has('age')) out.add('dates');
    if (has('birthplace')) out.add('places');
    if (has('occupation')) out.add('otherEvents');
    if (has('tags')) out.add('tags');
    if (has('research')) ['sources', 'media', 'todos'].forEach((k) => out.add(k as keyof ExportPrivacy));
    if (has('relation')) out.add('friendsAndGroups');
    return out;
  })();
  const shownLabels = PRIVACY_LABELS.filter((l) => relevant.has(l.key) && (l.key !== 'hiddenNames' || hasHidden));
  const leftOut = PRIVACY_LABELS.filter((l) => !relevant.has(l.key) && (l.key !== 'hiddenNames' || hasHidden)).map((l) => l.label.toLowerCase());

  const build = async (): Promise<Built> => {
    const base = safeFileName(tree.name);
    const dated = `${base}-${isoDay()}`;
    if (format === 'native') {
      // A backup carries the media files too, so restoring it brings everything back.
      const files: Record<string, string> = {};
      for (const m of Object.values(t.media)) {
        if (!m.file || files[m.file.key]) continue;
        const blob = await getFile(m.file.key);
        if (blob) files[m.file.key] = await blobToDataUrl(blob);
      }
      return { name: dated + NATIVE_EXTENSION, blob: new Blob([exportNative(t, files)], { type: 'application/json' }) };
    }
    if (format === 'gedcom') {
      const ged = exportGedcom(t, { version: gedcom, includeHiddenNames: effective.hiddenNames, includePreserved, privatizeLiving: false });
      return { name: `${dated}${gedcom === '7.0' ? '-gedcom7' : ''}.ged`, blob: new Blob([ged], { type: 'text/plain;charset=utf-8' }) };
    }
    if (format === 'csv') {
      const text = sheet === 'people' ? exportCsv(t, effective) : exportRelationshipsCsv(t, effective);
      return { name: `${dated}-${sheet}.csv`, blob: new Blob([text], { type: 'text/csv;charset=utf-8' }) };
    }
    if (format === 'html') {
      // The page's chart uses the page's own colors, so it follows the reader's light or dark mode.
      const pageChart = htmlChart && focus ? wholeTreeSvg(t, { colors: CSS_VAR_COLORS, fields, heading: heading(), links: true }) : null;
      const html = htmlPage(t, pageChart, { light: palette(false), dark: palette(true), appearance: pageLook, privatized: effective.privatizeLiving });
      return { name: `${base}.html`, blob: new Blob([html], { type: 'text/html;charset=utf-8' }) };
    }
    if (!chart) throw new Error('There’s no one in the tree to chart yet.');
    if (format === 'svg') return { name: `${base}.svg`, blob: new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n', chart.svg], { type: 'image/svg+xml' }) };
    if (format === 'png') {
      const s = safeScale(chart.width, chart.height, scale);
      const png = await canvasBlob(await rasterize(chart, s, colors['--bg']), 'image/png');
      // Tagged with its resolution, so it prints at the size it looks on screen.
      return { name: `${base}.png`, blob: new Blob([withDpi(new Uint8Array(await png.arrayBuffer()), 96 * s)], { type: 'image/png' }) };
    }
    const p = plan!;
    // The footer names the tree when the PDF's built-in font can write it.
    const label = /^[ -ÿ’–—]*$/.test(tree.name) ? `${tree.name} · ` : '';
    const footer = (i: number, tile: Tile) => (p.tiles.length > 1 ? `${label}Page ${i + 1} of ${p.tiles.length} · row ${tile.row + 1}, column ${tile.col + 1}` : undefined);
    const detail = p.tiles.length > 1 ? plural(p.tiles.length, 'page') : undefined;
    let fonts: VectorFonts | undefined;
    try {
      fonts = await loadFonts();
    } catch {
      toast('Couldn’t load the PDF font, so the chart went in as a high-resolution picture instead.', 'info');
    }
    if (fonts) {
      const pdf = await vectorPdf(chart, p, await prepareAssets(chart, fonts), { title: tree.name, footer });
      return { name: `${base}.pdf`, blob: new Blob([pdf], { type: 'application/pdf' }), detail };
    }
    // Without the font: each page as a sharp picture, with its text invisible over it.
    const pages: PdfPage[] = [];
    for (const [i, tile] of p.tiles.entries()) {
      const part = p.tiles.length > 1 ? cropSvg(chart, { x: tile.sx, y: tile.sy, w: tile.sw, h: tile.sh }) : chart;
      const canvas = await rasterize(part, printScale(part, tile.k), colors['--bg']);
      const pixels = canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height).data;
      pages.push({
        width: p.width,
        height: p.height,
        image: await flateImage(pixels, canvas.width, canvas.height),
        x: tile.x,
        y: tile.y,
        w: tile.w,
        h: tile.h,
        footer: footer(i, tile),
        // The chart's text, invisible over the picture, so it can be selected, copied, and searched.
        texts: chart.texts
          .filter((r) => r.x >= tile.sx && r.x < tile.sx + tile.sw && r.y > tile.sy && r.y <= tile.sy + tile.sh)
          .map((r) => ({ x: tile.x + (r.x - tile.sx) * tile.k, y: tile.y + tile.h - (r.y - tile.sy) * tile.k, size: r.size * tile.k, width: r.width * tile.k, text: r.text })),
      });
    }
    return { name: `${base}.pdf`, blob: new Blob([pdfDocument(pages, tree.name)], { type: 'application/pdf' }), detail };
  };

  const remember = () => {
    const { hiddenNames: _, ...rest } = privacy;
    const saved: ExportPrefs = { format, privacy: rest, gedcom, sheet, who, scale, paper, look, pageLook, fields, htmlChart, includePreserved };
    lsSet(PREFS_KEY, JSON.stringify(saved));
  };
  const done = (message: string) => {
    remember();
    if (format === 'native' && includesEverything(effective)) markBackedUp(tree);
    closeDialog();
    toast(chart?.truncated ? `${message} The chart was limited to ${chart.people.toLocaleString('en-US')} people.` : message, 'success');
  };
  const fail = (e: unknown) => toast(`Export failed: ${(e as Error).message}`, 'error');
  const describe = (b: Built) => `${b.name}${b.detail ? ` (${b.detail})` : ''}`;

  const download = async () => {
    setBusy('download');
    try {
      const b = await build();
      saveBlob(b.name, b.blob);
      done(`Downloaded ${describe(b)}.`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const share = async () => {
    setBusy('share');
    try {
      const b = await build();
      const file = new File([b.blob], b.name, { type: b.blob.type });
      try {
        await navigator.share({ files: [file], title: tree.name });
        done(`Shared ${describe(b)}.`);
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        // Preparing a big file can outlast the browser's patience for a share; save it instead.
        saveBlob(b.name, b.blob);
        done(`Couldn’t open the share sheet, so ${describe(b)} was downloaded instead.`);
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    setBusy('copy');
    try {
      // The item is made right away (with the picture to follow) so the browser still counts the click.
      const blob = build().then((b) => b.blob);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      done('Copied the chart. Paste it into a message or document.');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const radio = (f: (typeof FORMATS)[number]) => (
    <label key={f.value} className="format-option">
      <input type="radio" name="fmt" checked={format === f.value} onChange={() => setFormat(f.value)} />
      <span>
        <b>{f.title}</b>
        <span className="muted small-text">{f.desc}</span>
      </span>
    </label>
  );
  const check = (key: keyof ExportPrivacy, label: string, hint?: string) => (
    <label key={key} className="check" title={hint}>
      <input
        type="checkbox"
        checked={effective[key]}
        onChange={(e) => {
          if (key === 'hiddenNames') setHiddenTouched(true);
          setPrivacy({ ...effective, [key]: e.target.checked });
        }}
      />
      {label}
    </label>
  );
  const option = (label: string, control: ReactNode) => (
    <div className="export-option">
      <span className="field-label">{label}</span>
      {control}
    </div>
  );
  const tiledPages = (p: PageSize) => (chart ? planPages(chart.width, chart.height, p, true).tiles.length : 0);
  const shareable = canShareFiles(info.mime);
  /** A picture needs someone to draw. */
  const empty = picFormat && !focus;

  return (
    <Modal
      title="Export"
      wide
      className="export-modal"
      footer={
        <>
          {format === 'png' && canCopyImage() && (
            <button className="btn left" onClick={copy} disabled={!!busy || empty}>
              <Copy size={15} /> {busy === 'copy' ? 'Copying…' : 'Copy image'}
            </button>
          )}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          {shareable && (
            <button className="btn" onClick={share} disabled={!!busy || empty}>
              <Share size={15} /> {busy === 'share' ? 'Preparing…' : 'Share…'}
            </button>
          )}
          <button className="btn primary" onClick={download} disabled={!!busy || empty}>
            <Download size={15} /> {busy === 'download' ? 'Preparing…' : 'Download'}
          </button>
        </>
      }
    >
      <div className="export-layout">
        <div className="stack tight export-col">
          <div className="field-label">Your data</div>
          {FORMATS.filter((f) => f.group === 'data').map(radio)}
          <div className="field-label" style={{ marginTop: 8 }}>Pages and pictures</div>
          {FORMATS.filter((f) => f.group === 'picture').map(radio)}
        </div>
        <div className="stack export-col">
          <fieldset className="settings-group stack tight">
            <legend className="field-label">{info.title}</legend>
            {format === 'gedcom' && (
              <>
                {option('Version', <Segmented<GedcomVersion> ariaLabel="GEDCOM version" value={gedcom} onChange={setGedcom} options={[{ value: '5.5.1', label: '5.5.1' }, { value: '7.0', label: '7.0' }]} />)}
                <div className="faint small-text">
                  {gedcom === '5.5.1'
                    ? 'Works with almost every genealogy program. Pronouns, friendships, and groups use extension tags others may ignore.'
                    : 'The newest standard, with nonbinary gender (SEX X). Fewer programs read it yet.'}
                </div>
                <label className="check">
                  <input type="checkbox" checked={includePreserved} onChange={(e) => setIncludePreserved(e.target.checked)} />
                  Records kept from an earlier GEDCOM import (sources, media, religious records)
                </label>
              </>
            )}
            {format === 'csv' && (
              <>
                {option('One row per', <Segmented<Sheet> ariaLabel="Spreadsheet rows" value={sheet} onChange={setSheet} options={[{ value: 'people', label: 'Person' }, { value: 'relationships', label: 'Relationship' }]} />)}
                <div className="faint small-text">
                  {sheet === 'people'
                    ? 'Names, dates, ages, life events, and each person’s relatives, with how each is related to the home person.'
                    : 'Each link (parent of, partner of, friend of) and group membership, with its type, status, and dates. For pivot tables and network tools.'}
                </div>
              </>
            )}
            {format === 'html' && (
              <>
                {option('Appearance', <Segmented<Appearance> ariaLabel="Appearance" value={pageLook} onChange={setPageLook} options={[{ value: 'auto', label: 'Automatic', title: 'Light or dark, following the reader’s device' }, { value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]} />)}
                <label className="check">
                  <input type="checkbox" checked={htmlChart} onChange={(e) => setHtmlChart(e.target.checked)} /> A chart of everyone, linked to each person’s details
                </label>
              </>
            )}
            {picFormat && focus && (
              <>
                {option('Who', <Segmented<Who> ariaLabel="Who to chart" value={who} onChange={setWho} options={[{ value: 'tree', label: 'Everyone' }, { value: 'view', label: `Around ${shortName(tree.people[focus])}`, title: 'As set up in the Tree view' }]} />)}
                {option('Appearance', <Segmented<'light' | 'dark'> ariaLabel="Appearance" value={look} onChange={setLook} options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]} />)}
              </>
            )}
            {format === 'png' && option('Size', <Segmented<string> ariaLabel="Resolution" value={String(scale)} onChange={(v) => setScale(Number(v))} options={[{ value: '1', label: 'Standard' }, { value: '2', label: 'Sharp' }, { value: '3', label: 'Extra sharp', title: 'For printing large' }]} />)}
            {format === 'pdf' && (
              <>
                <Select<Paper>
                  ariaLabel="Page size"
                  value={paper}
                  onChange={setPaper}
                  options={[
                    { value: 'fit', label: 'One page sized to the chart' },
                    { value: 'letter', label: 'One US Letter page' },
                    { value: 'a4', label: 'One A4 page' },
                    { value: 'letter-tiled', label: `US Letter pages at full size (${plural(tiledPages('letter'), 'page')})` },
                    { value: 'a4-tiled', label: `A4 pages at full size (${plural(tiledPages('a4'), 'page')})` },
                  ]}
                />
                {tiledFlag && <div className="faint small-text">Cards print at a readable size. Trim the margins and tape the pages together; each page’s footer says where it goes.</div>}
              </>
            )}
            {wantsChart && (
              <div className="export-option">
                <span className="field-label">On each card</span>
                <div className="card-fields">
                  {CARD_FIELDS.map((f) => (
                    <label key={f.key} className="check">
                      <input type="checkbox" checked={fields.includes(f.key)} onChange={(e) => setFields(CARD_FIELDS.map((x) => x.key).filter((k) => (k === f.key ? e.target.checked : fields.includes(k))))} />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {previewUrl && (
              <div className="export-preview">
                <img src={previewUrl} alt="Preview of the chart" />
              </div>
            )}
            {summary && <div className="callout small-text" role="status">{summary}</div>}
            {limits.map((l, i) => (
              <div key={i} className={`callout small-text${l.warn ? ' warn' : ''}`}>
                {l.text}
              </div>
            ))}
          </fieldset>
          <fieldset className="settings-group stack tight">
            <legend className="field-label">Privacy</legend>
            {check('privatizeLiving', 'Hide details of living people')}
            <div className="faint small-text">
              {effective.privatizeLiving
                ? 'Living people appear only as “Living,” with no names, dates, photos, or other details. Their relationships stay, so the shape of the family is kept. The choices below apply to everyone else.'
                : 'When on, living people appear only as “Living,” with their details left out.'}
            </div>
            {effective.privatizeLiving && unrecorded > 0 && (
              <div className="callout warn small-text">
                {plural(unrecorded, 'person has', 'people have')} no living status recorded, so their details are included. To hide them too, mark them as living (in People, tick them and choose Edit).
              </div>
            )}
          </fieldset>
          <fieldset className="settings-group stack tight">
            <legend className="field-label">What to include</legend>
            {shownLabels.length > 0 && <div className="include-grid">{shownLabels.map((l) => check(l.key, l.label, l.hint))}</div>}
            {leftOut.length > 0 && (
              <div className="faint small-text">
                {picFormat
                  ? 'Only what the cards show is listed. Names on cards are never hidden names.'
                  : `Not part of ${format === 'csv' ? 'this spreadsheet' : 'GEDCOM'}: ${andList(leftOut)}.`}
              </div>
            )}
            {effective.hiddenNames && hasHidden && relevant.has('hiddenNames') && format !== 'native' && <div className="callout warn small-text">Hidden names, including deadnames, will be visible to anyone who opens this file.</div>}
            {format === 'native' && hasHidden && effective.hiddenNames && <div className="faint small-text">Backups keep hidden names (still marked hidden) so restoring brings everything back.</div>}
          </fieldset>
        </div>
      </div>
    </Modal>
  );
}
